import { mapGetters, mapActions } from 'vuex'
import Modal from '@/components/modals/modal/modal.vue'
import FiltersSelect from '@/components/filters-select/filters-select.vue'
import emitHideMixin from '@/components/modals/mixins/emit-hide'

export default {
  name: 'EditSettingsModal',
  mixins: [emitHideMixin],
  components: {
    Modal,
    FiltersSelect
  },
  props: {
    schema: {
      type: Array,
      default: () => []
    },
    manufactures: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      formValues: {},
      originalValues: {},
      mixedFields: new Set(),
      pending: false
    }
  },
  computed: {
    ...mapGetters({
      selectedManufactoryIds: 'globalSettings/getSelectedManufactoryIds'
    }),
    selectedManufactories: {
      get() {
        return this.manufactures
          .filter(m => this.selectedManufactoryIds.includes(m.manufacture_id))
          .map(m => ({ manufacture_id: m.manufacture_id, manufacture_title: m.manufacture_title }))
      },
      set(value) {
        this.setSelectedManufactoryIds(value.map(v => v.manufacture_id))
      }
    },
    leftColumns() {
      return this.schema.filter(col => col.modal_column === 'left')
    },
    rightColumns() {
      return this.schema.filter(col => col.modal_column === 'right')
    },
    manufactoryOptions() {
      return this.manufactures
        .map(m => ({
          manufacture_id: m.manufacture_id,
          manufacture_title: m.manufacture_title
        }))
        .sort((a, b) => {
          const aSelected = this.selectedManufactoryIds.includes(a.manufacture_id)
          const bSelected = this.selectedManufactoryIds.includes(b.manufacture_id)
          if (aSelected === bSelected) return 0
          return aSelected ? -1 : 1
        })
    },
    isSaveDisabled() {
      if (this.pending) return true
      if (this.selectedManufactories.length === 0) return true

      for (const col of this.schema) {
        if (this.mixedFields.has(col.key)) continue
        const value = this.formValues[col.key]
        if (value === null || value === undefined || value === '') return true
      }

      return !this.hasChanges
    },
    hasChanges() {
      for (const col of this.schema) {
        if (this.mixedFields.has(col.key)) continue
        const current = this.formValues[col.key]
        const original = this.originalValues[col.key]
        if (original === null && current !== null && current !== '') return true
        if (String(current) !== String(original)) return true
      }
      return false
    }
  },
  methods: {
    ...mapActions({
      setSelectedManufactoryIds: 'globalSettings/setSelectedManufactoryIds'
    }),

    isMixedField(key) {
      return this.mixedFields.has(key)
    },

    onFieldInput(key) {
      if (this.mixedFields.has(key)) {
        this.mixedFields.delete(key)
        this.mixedFields = new Set(this.mixedFields)
      }
    },

    getSettingValues(key) {
      return this.selectedManufactories.map(selected => {
        const manufacture = this.manufactures.find(
          m => m.manufacture_id === selected.manufacture_id
        )
        return manufacture?.settings?.[key]
      }).filter(v => v !== undefined)
    },

    initForm() {
      this.formValues = {}
      this.originalValues = {}
      this.mixedFields = new Set()

      for (const col of this.schema) {
        const values = this.getSettingValues(col.key)
        const uniqueValues = [...new Set(values)]

        if (uniqueValues.length === 0) {
          this.$set(this.formValues, col.key, '')
          this.$set(this.originalValues, col.key, '')
        } else if (uniqueValues.length === 1) {
          this.$set(this.formValues, col.key, uniqueValues[0])
          this.$set(this.originalValues, col.key, uniqueValues[0])
        } else {
          this.mixedFields.add(col.key)
          this.$set(this.formValues, col.key, '')
          this.$set(this.originalValues, col.key, null)
        }
      }
    },

    show() {
      this.initForm()
      this.pending = false
      this.$refs.modal.show()
    },

    onHideModal() {
      this.formValues = {}
      this.originalValues = {}
      this.mixedFields = new Set()
      if (this.manuallyHide) {
        this.manuallyHide = false
        return
      }
      this.$emit('hide')
    },

    buildPayload() {
      const result = []

      for (const selected of this.selectedManufactories) {
        const manufacture = this.manufactures.find(m => m.manufacture_id === selected.manufacture_id)
        if (!manufacture) continue

        const changedSettings = {}
        for (const col of this.schema) {
          if (this.mixedFields.has(col.key)) continue
          const currentValue = this.formValues[col.key]
          if (currentValue === null || currentValue === undefined || currentValue === '') continue
          const originalValue = manufacture.settings?.[col.key]
          if (String(currentValue) === String(originalValue)) continue
          changedSettings[col.key] = String(currentValue)
        }

        if (Object.keys(changedSettings).length > 0) {
          result.push({ manufacture_id: selected.manufacture_id, settings: changedSettings })
        }
      }

      return { manufactures: result }
    },

    async onSubmit() {
      this.pending = true
      try {
        const payload = this.buildPayload()
        if (payload.manufactures.length > 0) {
          this.$emit('save', payload)
        }
        this.hide()
      } catch (error) {
        console.error('Failed to save settings:', error)
      } finally {
        this.pending = false
      }
    }
  },
  watch: {
    selectedManufactories: {
      handler: 'initForm',
      deep: true
    }
  }
}
